/**
 * Locale defaults + per-tenant resolver.
 *
 * Drives the BCP-47 locale passed to toLocaleDateString / toLocaleTimeString /
 * toLocaleString / Intl.NumberFormat across the app so dates, times, and
 * numbers format for the TENANT's region rather than a hardcoded 'en-AU'.
 *
 * Historical context (SaaS-readiness audit, May 2026):
 * The app hardcodes 'en-AU' in ~397 toLocale*() calls. That is correct for
 * Techery (an Australian tenant) but wrong the moment a non-AU tenant
 * onboards. This module is the single source of truth going forward; call
 * sites migrate from the literal 'en-AU' to getTenantLocale(tenant)
 * incrementally.
 *
 * TODO(saas-readiness): most call sites still pass the literal. A follow-up
 * sweep threads `tenant` into components (via useTenant) and services (via
 * a synchronous accessor) so per-tenant locale overrides take effect.
 */
import type { TenantConfig } from '@/types/tenant.types';

/** Platform default locale — Techery is an Australian business. */
export const DEFAULT_TENANT_LOCALE = 'en-AU';

/** localStorage key the active tenant's locale is cached under (see below). */
const LOCALE_STORAGE_KEY = 'tenant_locale';

/**
 * Resolve the formatting locale for a tenant. Missing tenant or missing
 * `business.locale` returns the platform default.
 *
 * Prefer this in React components (where `tenant` is available via useTenant)
 * so the value is reactive to tenant config changes.
 */
export function getTenantLocale(
  tenant: TenantConfig | null | undefined,
): string {
  const override = tenant?.business?.locale?.trim();
  return override || DEFAULT_TENANT_LOCALE;
}

/**
 * Synchronous locale accessor for pure helpers + services that lack React
 * context — the locale analogue of `getTenantIdSync()`. Reads the cached
 * value TenantContext writes on load (see `cacheTenantLocale`); falls back to
 * the platform default before the tenant has loaded. Lets formatting helpers
 * (fmtDate, money, etc.) become tenant-aware WITHOUT threading a `locale` param
 * through every caller.
 */
export function getLocaleSync(): string {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_TENANT_LOCALE;
  }
  return window.localStorage.getItem(LOCALE_STORAGE_KEY) || DEFAULT_TENANT_LOCALE;
}

/**
 * Cache the active tenant's locale to localStorage so `getLocaleSync()` can
 * read it. Called by TenantContext whenever a tenant config loads/updates.
 */
export function cacheTenantLocale(
  tenant: TenantConfig | null | undefined,
): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(LOCALE_STORAGE_KEY, getTenantLocale(tenant));
}
